import type { Locale } from "@zeeta/shared";

/** The company that operates Zeeta. Named only in the footer and on these legal pages. */
export const OPERATOR = {
  name: "Zeetabyte Digital",
  supportEmail: "support@zeetabyte.tech",
  privacyEmail: "privacy@zeetabyte.tech",
};

export const LEGAL_VERSION = { terms: "2026-10-22", privacy: "2026-10-22" };

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface LegalDocument {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

const termsEn: LegalDocument = {
  title: "Terms of Service",
  updated: "Last updated 22 October 2026",
  intro: `Zeeta is a product of ${OPERATOR.name} ("we", "us"). These terms govern your use of Zeeta's web application, point of sale, public API and hosted storefronts (together, "the Service"). By creating an account you agree to them.`,
  sections: [
    {
      heading: "1. Your account",
      paragraphs: [
        "You sign in with a Bangladeshi mobile number verified by a one-time code. You are responsible for activity under your account and for keeping your phone and devices secure.",
        "The person who creates a business on Zeeta is its owner. Owners decide who else can access the business and with which role, and are responsible for the actions of the staff they invite.",
      ],
    },
    {
      heading: "2. Your data",
      paragraphs: [
        "You own the business data you enter: products, stock, sales, customers, suppliers and accounts. We process it only to provide the Service, as described in our Privacy Policy.",
        "Each business's data is stored separately from every other business. Owners can export all of their data at any time.",
        "If you close your account, your business data is deleted 30 days after closure, except records we must keep by law, such as our own billing records.",
      ],
    },
    {
      heading: "3. Acceptable use",
      paragraphs: [
        "Do not use the Service for unlawful trade, to sell prohibited or unlicensed goods, to send unsolicited SMS, or to attempt to access another business's data or disrupt the Service.",
        "We may suspend a business that breaks these rules or that does not pay for a paid plan, after notifying the owner where the law and circumstances allow.",
      ],
    },
    {
      heading: "4. Plans and payment",
      paragraphs: [
        "Zeeta offers a free plan and paid plans with the limits shown on our pricing page. Paid plans renew for the period you choose until cancelled.",
        "If a payment is not received, a 7-day grace period applies. After that the business continues on free-plan limits in read-mostly mode; no data is deleted because of non-payment.",
        "SMS credits are prepaid and non-refundable once used.",
      ],
    },
    {
      heading: "5. Tax and compliance",
      paragraphs: [
        "Zeeta helps you produce VAT invoices and registers in National Board of Revenue formats. You remain responsible for the accuracy of the rates and information you enter and for filing your own returns.",
        "Pharmacies and other regulated businesses remain responsible for meeting the rules of their regulators.",
      ],
    },
    {
      heading: "6. Availability",
      paragraphs: [
        "We aim for 99.9% monthly availability and back up every business's data continuously. The point of sale keeps working offline and syncs when the connection returns.",
        "We are not liable for losses caused by events outside our reasonable control, including failures of mobile networks, payment providers or SMS gateways.",
      ],
    },
    {
      heading: "7. Liability",
      paragraphs: [
        "To the extent permitted by law, our total liability for any claim relating to the Service is limited to the fees you paid us in the 12 months before the claim.",
      ],
    },
    {
      heading: "8. Changes and governing law",
      paragraphs: [
        "We will notify owners in the app at least 14 days before material changes to these terms take effect.",
        "These terms are governed by the laws of Bangladesh. Disputes are subject to the jurisdiction of the courts of Dhaka.",
        `Questions: ${OPERATOR.supportEmail}`,
      ],
    },
  ],
};

const termsBn: LegalDocument = {
  title: "সেবার শর্তাবলি",
  updated: "সর্বশেষ হালনাগাদ ২২ অক্টোবর ২০২৬",
  intro: `জিটা ${OPERATOR.name}-এর ("আমরা") একটি পণ্য। জিটার ওয়েব অ্যাপ, পয়েন্ট অব সেল, পাবলিক API ও হোস্ট করা অনলাইন শপ (একত্রে "সেবা") ব্যবহারের ক্ষেত্রে এই শর্তাবলি প্রযোজ্য। অ্যাকাউন্ট খুললে আপনি এগুলোতে সম্মত হচ্ছেন।`,
  sections: [
    {
      heading: "১. আপনার অ্যাকাউন্ট",
      paragraphs: [
        "আপনি ওয়ান-টাইম কোড দিয়ে যাচাই করা বাংলাদেশি মোবাইল নম্বরে সাইন ইন করেন। আপনার অ্যাকাউন্টের সব কার্যক্রম এবং আপনার ফোন ও ডিভাইসের নিরাপত্তার দায়িত্ব আপনার।",
        "যিনি জিটায় ব্যবসা তৈরি করেন তিনি সেই ব্যবসার মালিক। মালিক ঠিক করেন আর কে কোন ভূমিকায় ব্যবসাটি ব্যবহার করবেন, এবং আমন্ত্রিত কর্মীদের কাজের দায়িত্বও তাঁর।",
      ],
    },
    {
      heading: "২. আপনার তথ্য",
      paragraphs: [
        "আপনার দেওয়া ব্যবসার তথ্য—পণ্য, স্টক, বিক্রি, ক্রেতা, সরবরাহকারী ও হিসাব—আপনার মালিকানাধীন। গোপনীয়তা নীতিতে বর্ণিত উপায়ে শুধু সেবা দেওয়ার জন্যই আমরা তা ব্যবহার করি।",
        "প্রতিটি ব্যবসার তথ্য অন্য সব ব্যবসা থেকে আলাদাভাবে সংরক্ষিত থাকে। মালিক যেকোনো সময় তাঁর সব তথ্য এক্সপোর্ট করতে পারেন।",
        "অ্যাকাউন্ট বন্ধ করলে ৩০ দিন পর আপনার ব্যবসার তথ্য মুছে ফেলা হয়; তবে আইন অনুযায়ী যে রেকর্ড রাখতে হয়, যেমন আমাদের বিলিং রেকর্ড, তা রাখা হয়।",
      ],
    },
    {
      heading: "৩. গ্রহণযোগ্য ব্যবহার",
      paragraphs: [
        "বেআইনি বাণিজ্য, নিষিদ্ধ বা লাইসেন্সবিহীন পণ্য বিক্রি, অযাচিত SMS পাঠানো, অন্য ব্যবসার তথ্যে প্রবেশের চেষ্টা বা সেবায় বিঘ্ন ঘটাতে সেবাটি ব্যবহার করা যাবে না।",
        "এই নিয়ম ভাঙলে বা পেইড প্ল্যানের মূল্য পরিশোধ না করলে, আইন ও পরিস্থিতি অনুযায়ী মালিককে জানিয়ে আমরা ব্যবসাটি স্থগিত করতে পারি।",
      ],
    },
    {
      heading: "৪. প্ল্যান ও পেমেন্ট",
      paragraphs: [
        "জিটায় একটি ফ্রি প্ল্যান এবং মূল্য পাতায় দেখানো সীমাসহ পেইড প্ল্যান রয়েছে। পেইড প্ল্যান বাতিল না করা পর্যন্ত আপনার বেছে নেওয়া মেয়াদে নবায়ন হয়।",
        "পেমেন্ট না পেলে ৭ দিনের গ্রেস পিরিয়ড থাকে। এরপর ব্যবসাটি ফ্রি প্ল্যানের সীমায় মূলত দেখার মোডে চলে; পেমেন্ট না করার কারণে কোনো তথ্য মোছা হয় না।",
        "SMS ক্রেডিট আগে কিনতে হয় এবং ব্যবহারের পর তা ফেরতযোগ্য নয়।",
      ],
    },
    {
      heading: "৫. কর ও নিয়ম পালন",
      paragraphs: [
        "জিটা জাতীয় রাজস্ব বোর্ডের ফরম্যাটে ভ্যাট ইনভয়েস ও রেজিস্টার তৈরিতে সাহায্য করে। আপনার দেওয়া হার ও তথ্যের নির্ভুলতা এবং নিজের রিটার্ন দাখিলের দায়িত্ব আপনার।",
        "ফার্মেসি ও অন্যান্য নিয়ন্ত্রিত ব্যবসাকে নিজ নিজ নিয়ন্ত্রক সংস্থার নিয়ম মেনে চলতে হবে।",
      ],
    },
    {
      heading: "৬. সেবার প্রাপ্যতা",
      paragraphs: [
        "আমরা মাসে ৯৯.৯% প্রাপ্যতার লক্ষ্য রাখি এবং প্রতিটি ব্যবসার তথ্য নিরবচ্ছিন্নভাবে ব্যাকআপ করি। ইন্টারনেট না থাকলেও পয়েন্ট অব সেল চলে এবং সংযোগ ফিরলে তথ্য সিঙ্ক হয়।",
        "মোবাইল নেটওয়ার্ক, পেমেন্ট প্রোভাইডার বা SMS গেটওয়ের ব্যর্থতাসহ আমাদের যুক্তিসংগত নিয়ন্ত্রণের বাইরের ঘটনায় সৃষ্ট ক্ষতির দায় আমাদের নয়।",
      ],
    },
    {
      heading: "৭. দায়বদ্ধতা",
      paragraphs: [
        "আইন যতটুকু অনুমতি দেয়, সেবা-সংক্রান্ত যেকোনো দাবিতে আমাদের মোট দায় দাবির আগের ১২ মাসে আপনার পরিশোধ করা ফি-এর মধ্যে সীমাবদ্ধ।",
      ],
    },
    {
      heading: "৮. পরিবর্তন ও প্রযোজ্য আইন",
      paragraphs: [
        "এই শর্তাবলিতে গুরুত্বপূর্ণ পরিবর্তন কার্যকর হওয়ার অন্তত ১৪ দিন আগে আমরা অ্যাপে মালিকদের জানাব।",
        "এই শর্তাবলি বাংলাদেশের আইন দ্বারা পরিচালিত। বিরোধ ঢাকার আদালতের এখতিয়ারাধীন।",
        `প্রশ্ন থাকলে: ${OPERATOR.supportEmail}`,
      ],
    },
  ],
};

const privacyEn: LegalDocument = {
  title: "Privacy Policy",
  updated: "Last updated 22 October 2026",
  intro: `Zeeta is a product of ${OPERATOR.name}, which is responsible for the personal data described here. We collect only what we need to run the Service.`,
  sections: [
    {
      heading: "1. What we collect",
      paragraphs: [
        "Account data: your mobile number, name, language and, if you add them, your email address.",
        "Security data: the devices and IP addresses you sign in from, and sign-in events, so you can review your sessions and we can detect misuse.",
        "Business data you enter, including your customers' and suppliers' names, phone numbers and addresses. For that data you are the controller and we process it on your instructions.",
      ],
    },
    {
      heading: "2. How we use it",
      paragraphs: [
        "To sign you in, run your business records, send the SMS and emails you or your business trigger, bill paid plans, provide support and keep the Service secure.",
        "We do not sell personal data and do not use your business data for advertising.",
      ],
    },
    {
      heading: "3. Where it is stored",
      paragraphs: [
        "Zeeta runs on Cloudflare's network. Each business's records are kept in a database of their own, encrypted in transit and at rest.",
        "We share data only with the providers needed to run the Service: Cloudflare (hosting), SMS aggregators (message delivery) and payment gateways (payments), each under contract.",
      ],
    },
    {
      heading: "4. Retention",
      paragraphs: [
        "Business records are kept while the account is open and for 30 days after closure. Sign-in records are kept for 12 months. Billing records are kept as long as the law requires.",
      ],
    },
    {
      heading: "5. Your choices",
      paragraphs: [
        "You can update your profile, sign out other devices, and export or delete business data from the app. A business owner can delete a customer's personal data on that customer's request.",
        `For any privacy request, write to ${OPERATOR.privacyEmail}. We reply within 30 days.`,
      ],
    },
  ],
};

const privacyBn: LegalDocument = {
  title: "গোপনীয়তা নীতি",
  updated: "সর্বশেষ হালনাগাদ ২২ অক্টোবর ২০২৬",
  intro: `জিটা ${OPERATOR.name}-এর একটি পণ্য; এখানে বর্ণিত ব্যক্তিগত তথ্যের দায়িত্ব তাদের। সেবা চালাতে যতটুকু দরকার, আমরা শুধু ততটুকু তথ্যই সংগ্রহ করি।`,
  sections: [
    {
      heading: "১. আমরা কী সংগ্রহ করি",
      paragraphs: [
        "অ্যাকাউন্টের তথ্য: আপনার মোবাইল নম্বর, নাম, ভাষা এবং যোগ করলে ইমেইল ঠিকানা।",
        "নিরাপত্তা তথ্য: যে ডিভাইস ও IP ঠিকানা থেকে সাইন ইন করেন এবং সাইন-ইনের ঘটনা, যাতে আপনি নিজের সেশন দেখতে পারেন এবং আমরা অপব্যবহার ধরতে পারি।",
        "আপনার দেওয়া ব্যবসার তথ্য, যার মধ্যে ক্রেতা ও সরবরাহকারীর নাম, ফোন নম্বর ও ঠিকানা আছে। এই তথ্যের নিয়ন্ত্রক আপনি; আমরা আপনার নির্দেশ অনুযায়ী তা প্রক্রিয়া করি।",
      ],
    },
    {
      heading: "২. আমরা কীভাবে ব্যবহার করি",
      paragraphs: [
        "আপনাকে সাইন ইন করাতে, ব্যবসার হিসাব চালাতে, আপনার বা আপনার ব্যবসার পাঠানো SMS ও ইমেইল পৌঁছাতে, পেইড প্ল্যানের বিল করতে, সহায়তা দিতে ও সেবা নিরাপদ রাখতে।",
        "আমরা ব্যক্তিগত তথ্য বিক্রি করি না এবং বিজ্ঞাপনের কাজে আপনার ব্যবসার তথ্য ব্যবহার করি না।",
      ],
    },
    {
      heading: "৩. কোথায় সংরক্ষিত হয়",
      paragraphs: [
        "জিটা Cloudflare-এর নেটওয়ার্কে চলে। প্রতিটি ব্যবসার রেকর্ড আলাদা ডেটাবেসে রাখা হয় এবং আদান-প্রদান ও সংরক্ষণের সময় এনক্রিপ্ট করা থাকে।",
        "সেবা চালাতে যাদের প্রয়োজন শুধু তাদের সঙ্গেই চুক্তির ভিত্তিতে তথ্য শেয়ার করা হয়: Cloudflare (হোস্টিং), SMS অ্যাগ্রিগেটর (মেসেজ পাঠানো) ও পেমেন্ট গেটওয়ে (পেমেন্ট)।",
      ],
    },
    {
      heading: "৪. কতদিন রাখা হয়",
      paragraphs: [
        "অ্যাকাউন্ট চালু থাকা পর্যন্ত এবং বন্ধের পর ৩০ দিন ব্যবসার রেকর্ড রাখা হয়। সাইন-ইনের রেকর্ড ১২ মাস রাখা হয়। বিলিং রেকর্ড আইন যতদিন চায় ততদিন রাখা হয়।",
      ],
    },
    {
      heading: "৫. আপনার অধিকার",
      paragraphs: [
        "অ্যাপ থেকেই আপনি প্রোফাইল হালনাগাদ করতে, অন্য ডিভাইস থেকে সাইন আউট করতে এবং ব্যবসার তথ্য এক্সপোর্ট বা মুছতে পারেন। কোনো ক্রেতা অনুরোধ করলে ব্যবসার মালিক তাঁর ব্যক্তিগত তথ্য মুছে দিতে পারেন।",
        `গোপনীয়তা-সংক্রান্ত যেকোনো অনুরোধ ${OPERATOR.privacyEmail}-এ লিখুন। আমরা ৩০ দিনের মধ্যে উত্তর দিই।`,
      ],
    },
  ],
};

export const LEGAL: Record<"terms" | "privacy", Record<Locale, LegalDocument>> = {
  terms: { en: termsEn, bn: termsBn },
  privacy: { en: privacyEn, bn: privacyBn },
};
